import { useEffect, useState } from "react";
import { listLeads, qualifyLeadChat } from "../api/leadApi";
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Button,
  Alert,
  Typography,
  Box,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
} from "@mui/material";

const Dashboard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scoreFilter, setScoreFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loadingQualify, setLoadingQualify] = useState("");

  const fetchLeads = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (scoreFilter) params.score = scoreFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await listLeads(Object.keys(params).length ? params : undefined);
      setLeads(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [scoreFilter, statusFilter]);

  const handleQualify = async (leadId) => {
    setLoadingQualify(leadId);
    try {
      await qualifyLeadChat(leadId);
      await fetchLeads();
    } catch (err) {
      alert(err.response?.data?.detail || "Qualification failed");
    } finally {
      setLoadingQualify("");
    }
  };

  const getScoreColor = (score) => {
    switch (score?.toLowerCase()) {
      case "hot":
        return "#d32f2f";
      case "warm":
        return "#f57c00";
      case "cold":
        return "#1976d2";
      default:
        return "inherit";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header and Filters */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 2 }}>
          Lead Management Dashboard
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Score</InputLabel>
            <Select
              value={scoreFilter}
              onChange={(e) => setScoreFilter(e.target.value)}
              label="Score"
            >
              <MenuItem value="">All Scores</MenuItem>
              <MenuItem value="Hot">Hot</MenuItem>
              <MenuItem value="Warm">Warm</MenuItem>
              <MenuItem value="Cold">Cold</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="New">New</MenuItem>
              <MenuItem value="Qualified">Qualified</MenuItem>
              <MenuItem value="Scheduled">Scheduled</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="primary"
            onClick={fetchLeads}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Score</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Budget</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Timeline</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.length > 0 ? (
                leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>{lead.name}</TableCell>
                    <TableCell>{lead.phone}</TableCell>
                    <TableCell>{lead.status || "—"}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 2,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: getScoreColor(lead.qualification_score),
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        {lead.qualification_score || "—"}
                      </Box>
                    </TableCell>
                    <TableCell>{lead.budget || "—"}</TableCell>
                    <TableCell>{lead.timeline || "—"}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => handleQualify(lead.id)}
                        disabled={loadingQualify === lead.id}
                      >
                        {loadingQualify === lead.id ? (
                          <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                          "AI Qualify"
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      No leads found matching your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};
export default Dashboard;